'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { MessageSquare, Plus, X, Send, CheckCircle, User, ExternalLink, Filter } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { useSSE } from '@/hooks/useSSE';

interface Thread {
  id: string;
  patientId: string;
  patientName: string;
  createdByOperatorName: string;
  subject: string;
  messages: ThreadMessage[];
  isResolved: boolean;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ThreadMessage {
  id: string;
  fromOperatorName: string;
  content: string;
  reactions: { emoji: string; operatorName: string }[];
  createdAt: string;
}

interface Patient {
  id: string;
  name: string;
}

const QUICK_REACTIONS = ['👍', '✅', '🔴', '⚠️', '💬'];

export default function PatientThreadsPage() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ patientId: '', subject: '' });
  const [replyText, setReplyText] = useState('');
  const [saving, setSaving] = useState(false);
  const [filterUnresolved, setFilterUnresolved] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 20;

  const operatorId = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('lcm_session') ?? '{}')?.operator?.id ?? 'op-1' : 'op-1';
  const operatorName = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('lcm_session') ?? '{}')?.operator?.name ?? 'Operatore' : 'Operatore';

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), perPage: String(perPage) });
      if (filterUnresolved) params.set('isResolved', 'false');
      const [threadsRes, patientsRes] = await Promise.all([
        fetch(`/api/patients/threads?${params}`),
        fetch('/api/patients?perPage=50'),
      ]);
      const threadsJson = await threadsRes.json();
      const patientsJson = await patientsRes.json();
      const loadedThreads: Thread[] = threadsJson.data ?? [];
      setThreads(loadedThreads);
      setPatients(patientsJson.data ?? []);
      setSelectedThread(prev => prev ? (loadedThreads.find(t => t.id === prev.id) ?? null) : null);
    } catch { /* keep */ } finally { setLoading(false); }
  }, [page, filterUnresolved]);

  useEffect(() => { load(); }, [load]);

  // Replace 10s polling with SSE
  useSSE({
    onActivity: () => load(),
    onMetrics: () => load(),
    enabled: true,
  });

  const handleCreateThread = async () => {
    if (!form.patientId || !form.subject.trim()) { toast.error('Seleziona paziente e inserisci oggetto'); return; }
    setSaving(true);
    try {
      const patient = patients.find(p => p.id === form.patientId);
      const res = await fetch('/api/patients/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', patientId: form.patientId, patientName: patient?.name ?? '', createdByOperatorId: operatorId, createdByOperatorName: operatorName, subject: form.subject }),
      });
      if (!res.ok) throw new Error();
      toast.success('Thread creato');
      setShowForm(false);
      setForm({ patientId: '', subject: '' });
      load();
    } catch { toast.error('Errore nella creazione'); } finally { setSaving(false); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedThread) return;
    try {
      const res = await fetch('/api/patients/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add_message', threadId: selectedThread.id, fromOperatorId: operatorId, fromOperatorName: operatorName, content: replyText.trim() }),
      });
      if (!res.ok) throw new Error();
      setReplyText('');
      load();
    } catch { toast.error('Errore nell\'invio'); }
  };

  const handleReaction = async (messageId: string, emoji: string) => {
    try {
      const res = await fetch('/api/operators/chat-reactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId, operatorId, operatorName, emoji }),
      });
      if (!res.ok) throw new Error();
      // Optimistically update the reaction in the selected thread
      setSelectedThread(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map(m => {
            if (m.id !== messageId) return m;
            const alreadyReacted = m.reactions.some(r => r.operatorName === operatorName && r.emoji === emoji);
            if (alreadyReacted) {
              return { ...m, reactions: m.reactions.filter(r => !(r.operatorName === operatorName && r.emoji === emoji)) };
            }
            return { ...m, reactions: [...m.reactions, { emoji, operatorName }] };
          }),
        };
      });
    } catch { /* ignore */ }
  };

  const handleResolve = async (threadId: string) => {
    try {
      await fetch('/api/patients/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'resolve', threadId }),
      });
      toast.success('Thread risolto');
      setSelectedThread(null);
      load();
    } catch { toast.error('Errore'); }
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        <div className="px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Thread Discussione Pazienti</h1>
              <p className="text-sm text-muted-foreground mt-1">Discussioni interne collegate al profilo paziente</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setFilterUnresolved(f => !f); setPage(1); }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${filterUnresolved ? 'bg-primary/10 border-primary text-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
              >
                <Filter size={13} /> {filterUnresolved ? 'Solo aperti' : 'Tutti'}
              </button>
              <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-white text-sm hover:bg-primary/90 transition-colors">
                <Plus size={14} /> Nuovo Thread
              </button>
            </div>
          </div>

          {showForm && (
            <div className="bg-white border border-border rounded-xl p-5 mb-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">Nuovo Thread</h3>
                <button onClick={() => setShowForm(false)}><X size={16} className="text-muted-foreground" /></button>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Paziente</label>
                  <select value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20">
                    <option value="">Seleziona...</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Oggetto</label>
                  <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="Oggetto della discussione..."
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20" />
                </div>
              </div>
              <div className="flex justify-end">
                <button onClick={handleCreateThread} disabled={saving}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors">
                  {saving ? 'Creazione...' : 'Crea Thread'}
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-4 h-[calc(100vh-260px)]">
            {/* Thread list */}
            <div className="w-72 bg-white border border-border rounded-xl flex flex-col overflow-hidden">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Thread ({threads.length})</p>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-border">
                {loading ? (
                  <div className="py-8 text-center"><div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" /></div>
                ) : threads.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-xs">Nessun thread</div>
                ) : threads.map(t => (
                  <button key={t.id} onClick={() => setSelectedThread(t)}
                    className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${selectedThread?.id === t.id ? 'bg-primary/10' : ''}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <User size={12} className="text-muted-foreground shrink-0" />
                      <span className="text-xs font-medium text-foreground truncate">{t.patientName}</span>
                      {t.isResolved && <CheckCircle size={10} className="text-green-500 shrink-0" />}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{t.subject}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{t.messages.length} messaggi</p>
                  </button>
                ))}
              </div>
              {/* Pagination */}
              <div className="p-2 border-t border-border flex items-center justify-between">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="text-xs px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors">←</button>
                <span className="text-xs text-muted-foreground">Pag. {page}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={threads.length < perPage}
                  className="text-xs px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors">→</button>
              </div>
            </div>

            {/* Thread detail */}
            <div className="flex-1 bg-white border border-border rounded-xl flex flex-col overflow-hidden">
              {!selectedThread ? (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Seleziona un thread</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="p-4 border-b border-border flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground">{selectedThread.patientName}</p>
                        {selectedThread.patientId && (
                          <Link href={`/patients/${selectedThread.patientId}`}
                            className="flex items-center gap-1 text-xs text-primary hover:underline">
                            <ExternalLink size={11} /> Profilo
                          </Link>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{selectedThread.subject}</p>
                    </div>
                    {!selectedThread.isResolved && (
                      <button onClick={() => handleResolve(selectedThread.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-green-300 text-green-700 rounded-lg text-xs hover:bg-green-50 transition-colors">
                        <CheckCircle size={12} /> Risolvi
                      </button>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {selectedThread.messages.length === 0 ? (
                      <div className="text-center text-muted-foreground text-sm py-8">Nessun messaggio ancora</div>
                    ) : selectedThread.messages.map(msg => (
                      <div key={msg.id} className="bg-muted/30 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-foreground">{msg.fromOperatorName}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(msg.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="text-sm text-foreground">{msg.content}</p>
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          {QUICK_REACTIONS.map(emoji => {
                            const count = msg.reactions.filter(r => r.emoji === emoji).length;
                            return (
                              <button key={emoji} onClick={() => handleReaction(msg.id, emoji)}
                                className={`text-xs px-1.5 py-0.5 rounded-full border transition-colors ${count > 0 ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border hover:bg-muted'}`}>
                                {emoji}{count > 0 && <span className="ml-0.5">{count}</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  {!selectedThread.isResolved && (
                    <div className="p-3 border-t border-border flex gap-2">
                      <input value={replyText} onChange={e => setReplyText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                        placeholder="Scrivi un messaggio..."
                        className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20" />
                      <button onClick={handleReply} disabled={!replyText.trim()}
                        className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors">
                        <Send size={14} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
